import React from 'react';
import { Link } from 'react-router-dom';
import './LegalPage.css';

export default function PrivacyPolicy() {
  return (
    <div className="legal-page">

      {/* Hero Banner */}
      <div className="legal-hero">
        <div className="legal-hero-grid" />
        <div className="legal-hero-orb" />
        <div className="legal-hero-content">
          <div className="legal-breadcrumb">
            <Link to="/">Home</Link>
            <span className="legal-breadcrumb-sep">→</span>
            <span>Privacy Policy</span>
          </div>
          <h1 className="legal-hero-title">Privacy Policy</h1>
          <p className="legal-hero-sub">
            Kelwa Technologies Private Limited is committed to protecting your
            personal information and your right to privacy.
          </p>
          <div className="legal-effective">
            <span>Effective: 01 January 2025</span>
            <div className="legal-effective-dot" />
            <span>Last Updated: 01 March 2025</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="legal-body">
        <div className="legal-body-inner">

          {/* Sidebar TOC */}
          <aside className="legal-toc">
            <div className="legal-toc-title">Contents</div>
            <ol className="legal-toc-list">
              {[
                'Introduction',
                'Information We Collect',
                'How We Use Your Information',
                'Legal Basis for Processing',
                'Data Sharing and Disclosure',
                'Data Retention',
                'Security',
                'Your Rights',
                'Cookies',
                'Changes to This Policy',
                'Contact',
              ].map((item, i) => (
                <li key={i}><a href={`#pp-${i + 1}`}>{i + 1}. {item}</a></li>
              ))}
            </ol>
          </aside>

          {/* Main Content */}
          <article className="legal-content">

            <section className="legal-section" id="pp-1">
              <h2>1. Introduction</h2>
              <p>Kelwa Technologies Private Limited ("Company", "we", "our", or "us") is committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or engage with our services.</p>
              <p>Please read this policy carefully. If you disagree with its terms, please discontinue use of our services. This policy applies to all information collected through our website and any related services.</p>
            </section>

            <section className="legal-section" id="pp-2">
              <h2>2. Information We Collect</h2>
              <p>We may collect information that you provide directly to us, including:</p>
              <ul>
                <li><strong>Personal identification information</strong> — name, email address, phone number, and postal address</li>
                <li><strong>Business information</strong> — company name, designation, and industry</li>
                <li><strong>Communication data</strong> — messages, enquiries, and feedback submitted through our website</li>
                <li><strong>Technical data</strong> — IP address, browser type and version, device information, time zone, operating system, and cookies</li>
                <li><strong>Usage data</strong> — information about how you use our website and services</li>
              </ul>
              <p>We collect this information only when you voluntarily provide it to us, for example when filling out our contact form or corresponding with us by email.</p>
            </section>

            <section className="legal-section" id="pp-3">
              <h2>3. How We Use Your Information</h2>
              <p>We use the information we collect for the following purposes:</p>
              <ul>
                <li>To provide, operate, and maintain our services</li>
                <li>To respond to your enquiries and customer service requests</li>
                <li>To send you administrative information, including service updates and policy changes</li>
                <li>To improve our website and services through analysis and research</li>
                <li>To comply with applicable legal and regulatory obligations</li>
                <li>To detect, prevent, and address fraudulent or malicious activity</li>
                <li>To fulfil any other purpose for which you provide information</li>
              </ul>
              <p>We will not use your personal information for any purpose not listed above without first obtaining your consent.</p>
            </section>

            <section className="legal-section" id="pp-4">
              <h2>4. Legal Basis for Processing</h2>
              <p>We process your personal data under the following legal bases in accordance with applicable Indian data protection law and the Information Technology Act, 2000:</p>
              <ul>
                <li><strong>Consent</strong> — where you have given explicit consent for processing</li>
                <li><strong>Contract performance</strong> — where processing is necessary to fulfil a contractual obligation</li>
                <li><strong>Legal obligation</strong> — where processing is necessary to comply with applicable law</li>
                <li><strong>Legitimate interests</strong> — where processing is in our legitimate business interests and does not override your rights</li>
              </ul>
            </section>

            <section className="legal-section" id="pp-5">
              <h2>5. Data Sharing and Disclosure</h2>
              <p>We do not sell, trade, rent, or otherwise transfer your personal information to third parties, except in the following circumstances:</p>
              <ul>
                <li><strong>Service providers</strong> — trusted third parties who assist in operating our business, subject to confidentiality agreements</li>
                <li><strong>Legal requirements</strong> — when required by law, regulation, court order, or governmental authority</li>
                <li><strong>Safety and protection</strong> — to protect the rights, property, or safety of the Company, our clients, or others</li>
                <li><strong>Business transfers</strong> — in connection with a merger, acquisition, or sale of all or substantially all of our assets</li>
                <li><strong>Consent</strong> — with your explicit prior consent for any other disclosure</li>
              </ul>
            </section>

            <section className="legal-section" id="pp-6">
              <h2>6. Data Retention</h2>
              <p>We retain your personal information only for as long as is necessary to fulfil the purposes outlined in this policy, or as required by applicable laws and regulations. The criteria we use to determine retention periods include:</p>
              <ul>
                <li>The nature of the personal data and the purposes for which it is collected</li>
                <li>Legal or regulatory requirements to retain data for a specific minimum period</li>
                <li>Whether the retention is necessary to resolve disputes or enforce our agreements</li>
              </ul>
              <p>When personal data is no longer needed, we ensure it is securely deleted, destroyed, or anonymised in an irreversible manner.</p>
            </section>

            <section className="legal-section" id="pp-7">
              <h2>7. Security</h2>
              <p>We implement appropriate technical and organisational security measures to protect your personal information against unauthorised access, alteration, disclosure, or destruction. These measures include:</p>
              <ul>
                <li>Encryption of data in transit using industry-standard TLS protocols</li>
                <li>Access controls and authentication requirements for systems containing personal data</li>
                <li>Regular security assessments and audits</li>
                <li>Employee training on data protection and privacy practices</li>
              </ul>
              <p>However, no method of transmission over the internet or method of electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your personal information, we cannot guarantee its absolute security.</p>
            </section>

            <section className="legal-section" id="pp-8">
              <h2>8. Your Rights</h2>
              <p>Subject to applicable law, you may have the following rights with respect to your personal information:</p>
              <ul>
                <li><strong>Right of access</strong> — to request a copy of the personal information we hold about you</li>
                <li><strong>Right to rectification</strong> — to request correction of inaccurate or incomplete data</li>
                <li><strong>Right to erasure</strong> — to request deletion of your personal data in certain circumstances</li>
                <li><strong>Right to withdraw consent</strong> — to withdraw consent at any time where processing is based on consent</li>
                <li><strong>Right to lodge a complaint</strong> — with the relevant data protection supervisory authority</li>
              </ul>
              <p>To exercise any of these rights, please contact us using the details provided in the Contact section below.</p>
            </section>

            <section className="legal-section" id="pp-9">
              <h2>9. Cookies</h2>
              <p>Our website may use cookies and similar tracking technologies to enhance your browsing experience, analyse site traffic, and personalise content. The types of cookies we may use include:</p>
              <ul>
                <li><strong>Essential cookies</strong> — necessary for the website to function properly</li>
                <li><strong>Analytics cookies</strong> — to understand how visitors interact with our website</li>
                <li><strong>Preference cookies</strong> — to remember your settings and preferences</li>
              </ul>
              <p>You may configure your browser to refuse cookies or to alert you when cookies are being sent. Please note that some parts of our website may not function properly if you disable cookies.</p>
            </section>

            <section className="legal-section" id="pp-10">
              <h2>10. Changes to This Policy</h2>
              <p>We reserve the right to update or modify this Privacy Policy at any time. We will notify you of material changes by posting the updated policy on our website with a revised effective date. We encourage you to review this policy periodically to stay informed about how we protect your information.</p>
              <p>Your continued use of our services following any changes to this policy will constitute your acceptance of those changes.</p>
            </section>

            <section className="legal-section" id="pp-11">
              <h2>11. Contact</h2>
              <p>If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:</p>
              <div className="legal-contact-box">
                <div className="legal-contact-row">
                  <span className="legal-contact-label">Company</span>
                  <span>Kelwa Technologies Private Limited</span>
                </div>
                <div className="legal-contact-row">
                  <span className="legal-contact-label">Email</span>
                  <span>privacy@kelwatechnologies.in</span>
                </div>
                <div className="legal-contact-row">
                  <span className="legal-contact-label">Country</span>
                  <span>India</span>
                </div>
              </div>
            </section>

          </article>
        </div>
      </div>

    </div>
  );
}
